import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Copy, Check, Share2, Crown } from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fireConfetti = () => {
  const colors = ['#FF3D00', '#FFB300', '#FFFFFF', '#000000'];
  const duration = 1500;
  const end = Date.now() + duration;

  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 65, origin: { x: 0, y: 0.7 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 65, origin: { x: 1, y: 0.7 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();

  setTimeout(() => {
    confetti({ particleCount: 80, spread: 90, origin: { y: 0.6 }, colors, scalar: 1.1 });
  }, 100);
};

const getRefFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('ref');
};

const WaitlistForm = ({
  variant = 'default',
  counterColor = '#FF3D00',
  textColor = 'white',
  source = 'homepage',
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [count, setCount] = useState(1432);
  const [signupResult, setSignupResult] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [copied, setCopied] = useState(false);

  const isHero = variant === 'hero';

  useEffect(() => {
    fetchCount();
  }, []);

  const fetchCount = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/count`);
      setCount(response.data.count);
    } catch (error) {
      console.error('Error fetching count:', error);
    }
  };

  const fetchReferralStats = async (code) => {
    try {
      const res = await axios.get(`${API}/referral/${code}`);
      setReferralStats(res.data);
    } catch (e) {
      console.error('Error fetching referral stats', e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const referredBy = getRefFromUrl();
      const res = await axios.post(`${API}/waitlist`, {
        name: name.trim() || null,
        email,
        source,
        referred_by: referredBy ? referredBy.toUpperCase() : null,
      });
      setSignupResult(res.data);
      setMessage("You're in! Welcome to the founding batch.");
      setName('');
      setEmail('');
      fetchCount();
      fireConfetti();
      if (res.data.referral_code) fetchReferralStats(res.data.referral_code);
    } catch (error) {
      if (error.response?.status === 400) setMessage('Email already registered!');
      else setMessage('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const getReferralLink = () => {
    if (!signupResult?.referral_code) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/?ref=${signupResult.referral_code}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getReferralLink());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { console.error('Copy failed', e); }
  };

  const handleShare = async () => {
    const link = getReferralLink();
    const shareText = "I just got on the Way Big waitlist — cream biscuits with 10g protein, 3g fibre, under 200 kcal, no palm oil, no refined sugar. Use my link to join.";
    if (navigator.share) {
      try { await navigator.share({ title: 'Way Big', text: shareText, url: link }); } catch (e) {}
    } else {
      handleCopy();
    }
  };

  // SUCCESS STATE — referral card
  if (signupResult) {
    const referralCount = referralStats?.referrals_count || 0;
    const isFoundingMember = referralStats?.is_founding_member || false;
    const remaining = Math.max(0, 3 - referralCount);
    const greeting = signupResult.name ? `You're in, ${signupResult.name.split(' ')[0]}.` : "You're in.";

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl mx-auto"
        data-testid="signup-success-card"
      >
        <div className="bg-black border-4 border-black text-white p-6 sm:p-8" style={{ boxShadow: '8px 8px 0px rgba(0,0,0,0.3)' }}>
          {isFoundingMember && (
            <div className="inline-flex items-center gap-2 bg-[#FFB300] text-black px-3 py-1 font-['Outfit'] font-bold uppercase text-xs tracking-[0.2em] mb-4">
              <Crown size={14} /> Founding Member
            </div>
          )}

          <h3 className="font-['Bebas_Neue'] text-3xl sm:text-4xl text-white uppercase mb-2" data-testid="signup-success-heading">
            {greeting} <span className="text-[#FF3D00]">Now help us drop bigger.</span>
          </h3>
          <p className="font-['Manrope'] text-white/70 mb-6 text-sm sm:text-base">
            Refer 3 friends and unlock <span className="text-[#FFB300] font-bold">Founding Member</span> status — special launch pricing + first-batch reservation.
          </p>

          <div className="mb-5">
            <div className="flex justify-between items-center mb-2">
              <span className="font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300]">Your Progress</span>
              <span className="font-['Bebas_Neue'] text-xl text-white" data-testid="referral-progress">{referralCount} / 3</span>
            </div>
            <div className="h-3 bg-white/10 overflow-hidden border border-white/20">
              <motion.div
                className={`h-full ${isFoundingMember ? 'bg-[#FFB300]' : 'bg-[#FF3D00]'}`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min((referralCount / 3) * 100, 100)}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            {!isFoundingMember && remaining > 0 && (
              <p className="mt-2 text-xs text-white/50 font-['Manrope']">{remaining} more {remaining === 1 ? 'friend' : 'friends'} to unlock Founding Member.</p>
            )}
          </div>

          <div className="mb-4">
            <label className="block font-['Outfit'] text-xs uppercase tracking-[0.2em] text-[#FFB300] mb-2">Your Referral Link</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                data-testid="referral-link-input"
                type="text"
                readOnly
                value={getReferralLink()}
                className="flex-1 bg-black/60 border-2 border-[#2A2A2A] text-white font-['Manrope'] text-sm px-4 py-3 outline-none"
                onClick={(e) => e.target.select()}
              />
              <button
                data-testid="copy-referral-button"
                onClick={handleCopy}
                data-cursor={copied ? 'COPIED' : 'COPY'}
                className="flex items-center justify-center gap-2 bg-[#FF3D00] hover:bg-[#FFB300] text-black font-['Bebas_Neue'] text-lg tracking-wider py-3 px-6 border-2 border-[#FF3D00] hover:border-[#FFB300] min-w-[120px]"
              >
                {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy</>}
              </button>
            </div>
          </div>

          <button
            data-testid="share-referral-button"
            onClick={handleShare}
            data-cursor="SHARE"
            className="w-full flex items-center justify-center gap-2 bg-transparent hover:bg-white/5 text-white font-['Outfit'] font-bold text-sm uppercase tracking-[0.1em] py-3 px-6 border-2 border-white"
          >
            <Share2 size={14} /> Share with friends
          </button>
        </div>
      </motion.div>
    );
  }

  // HERO variant — compact, integrated into hero
  if (isHero) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="w-full max-w-md"
        data-testid="hero-waitlist-form-wrapper"
      >
        <div className="relative">
          {/* Subtle pulsing glow ring */}
          <motion.div
            aria-hidden
            className="absolute -inset-1 bg-[#FF3D00] opacity-20 blur-md pointer-events-none"
            animate={{ opacity: [0.15, 0.35, 0.15] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="relative bg-black border-2 border-[#FF3D00] p-6 sm:p-7">
            <div className="flex items-center gap-2 mb-5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3D00] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF3D00]"></span>
              </span>
              <span className="font-['Outfit'] text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#FFB300]">
                Founding batch · Limited
              </span>
            </div>

            <h3 className="font-['Bebas_Neue'] text-2xl sm:text-3xl text-white uppercase tracking-tight leading-none mb-4">
              Get on the list.
            </h3>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                data-testid="hero-waitlist-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your first name"
                className="w-full bg-black/60 border border-[#2A2A2A] focus:border-[#FF3D00] text-white font-['Manrope'] text-base placeholder:text-white/30 px-4 py-3 outline-none transition-colors"
              />
              <input
                data-testid="hero-waitlist-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                className="w-full bg-black/60 border border-[#2A2A2A] focus:border-[#FF3D00] text-white font-['Manrope'] text-base placeholder:text-white/30 px-4 py-3 outline-none transition-colors"
              />
              <button
                data-testid="hero-waitlist-submit-button"
                type="submit"
                disabled={loading}
                data-cursor="JOIN"
                className="w-full bg-[#FF3D00] hover:bg-[#FFB300] text-black font-['Bebas_Neue'] text-xl uppercase tracking-wider py-3 px-6 border-2 border-[#FF3D00] hover:border-[#FFB300] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Joining…' : "Count me in"}
              </button>
            </form>

            {message && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-3 text-center font-['Manrope'] text-sm text-[#FF3D00]"
                data-testid="hero-waitlist-message"
              >
                {message}
              </motion.p>
            )}

            <div className="mt-4 flex items-center justify-between text-[11px] sm:text-xs font-['Manrope']">
              <span className="text-white/50">No spam · Just biscuits</span>
              <span className="flex items-center gap-1.5 text-white/70">
                <span className="text-[#FF3D00] font-['Bebas_Neue'] text-lg leading-none">
                  <AnimatedCounter value={count} duration={2} testId="hero-counter-number" />
                </span>
                <span>on the list</span>
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // DEFAULT variant — the big "Be First" section
  return (
    <div className="w-full max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              data-testid="waitlist-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your first name"
              className="w-full bg-black border-2 border-[#2A2A2A] focus:border-[#FF3D00] text-white font-['Manrope'] text-lg px-6 py-4 outline-none"
            />
            <input
              data-testid="waitlist-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
              className="w-full bg-black border-2 border-[#2A2A2A] focus:border-[#FF3D00] text-white font-['Manrope'] text-lg px-6 py-4 outline-none"
            />
          </div>
          
          <button
            data-testid="waitlist-submit-button"
            type="submit"
            disabled={loading}
            data-cursor="JOIN"
            className="w-full bg-[#FF3D00] hover:bg-[#FFB300] text-black font-['Bebas_Neue'] text-2xl uppercase tracking-wider py-4 px-8 border-2 border-[#FF3D00] hover:border-[#FFB300] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'JOINING...' : "I'M IN"}
          </button>
        </form>

        {message && (
          <motion.p
            data-testid="waitlist-message"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 text-center font-['Manrope'] text-[#FF3D00]"
          >
            {message}
          </motion.p>
        )}

        <p className="text-center text-sm mt-4 font-['Manrope']" style={{ color: textColor, opacity: 0.5 }}>No spam. Just biscuits.</p>
        
        <motion.div
          data-testid="waitlist-counter"
          className="text-center mt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <span style={{ color: counterColor }}>
            <AnimatedCounter value={count} duration={2} className="font-['Bebas_Neue'] text-3xl" testId="waitlist-counter-number" />
          </span>
          <span className="font-['Manrope'] text-sm ml-2" style={{ color: textColor, opacity: 0.6 }}>people already waiting</span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default WaitlistForm;
